import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import { GalleryImage } from "../../types/components/imageGallery";

interface ImageGalleryContextValue {
  images: GalleryImage[];
  displayedImageIndex: number;
  setDisplayedImageIndex: Dispatch<SetStateAction<number>>;
  isSwipeDisabled: boolean;
  setIsSwipeDisabled: Dispatch<SetStateAction<boolean>>;
  shouldAnimateTransition: boolean;
  resumeTransitions: () => void;
  goToNext: () => void;
  goToPrevious: () => void;
}

const ImageGalleryContext = createContext<ImageGalleryContextValue | null>(
  null,
);

interface ImageGalleryProviderProps {
  children: ReactNode;
  images: GalleryImage[];
  initialIndex?: number;
}

export const ImageGalleryProvider = ({
  children,
  images,
  initialIndex = 0,
}: ImageGalleryProviderProps) => {
  const safeInitialIndex =
    initialIndex >= 0 && initialIndex < images.length ? initialIndex : 0;
  const [displayedImageIndex, setDisplayedImageIndex] =
    useState(safeInitialIndex);
  const [isSwipeDisabled, setIsSwipeDisabled] = useState(false);
  const [shouldAnimateTransition, setShouldAnimateTransition] = useState(false);
  const lastImageIndex = images.length - 1;

  const resumeTransitions = useCallback(() => {
    setTimeout(() => setShouldAnimateTransition(true));
  }, []);

  const goToNext = useCallback(() => {
    if (displayedImageIndex === lastImageIndex) {
      setShouldAnimateTransition(false);
      setDisplayedImageIndex(0);
    } else {
      setDisplayedImageIndex((currentIndex) => currentIndex + 1);
    }
  }, [displayedImageIndex, lastImageIndex]);

  const goToPrevious = useCallback(() => {
    if (displayedImageIndex === 0) {
      setShouldAnimateTransition(false);
      setDisplayedImageIndex(lastImageIndex);
    } else {
      setDisplayedImageIndex((currentIndex) => currentIndex - 1);
    }
  }, [displayedImageIndex, lastImageIndex]);

  useEffect(() => {
    if (displayedImageIndex >= images.length && images.length > 0) {
      setDisplayedImageIndex(images.length - 1);
    }
  }, [images, displayedImageIndex]);

  return (
    <ImageGalleryContext.Provider
      value={{
        images,
        displayedImageIndex,
        setDisplayedImageIndex,
        isSwipeDisabled,
        setIsSwipeDisabled,
        shouldAnimateTransition,
        resumeTransitions,
        goToNext,
        goToPrevious,
      }}
    >
      {children}
    </ImageGalleryContext.Provider>
  );
};

export const useImageGalleryContext = () => {
  const context = useContext(ImageGalleryContext);
  if (!context) {
    throw new Error(
      "useImageGalleryContext must be used within an ImageGalleryProvider",
    );
  }
  return context;
};
